import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@oniratec/onira-react-ui';
import {
  ChevronRightIcon,
  CircleHelp,
  LogOutIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  Settings,
} from 'lucide-react';
import { Link } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import { JSX, useEffect, useState } from 'react';
import { DropdownMenu as DropdownMenuPrimitive } from 'radix-ui';
import { NavigationLink } from '../NavigationLink';
import { useNavBarStore } from '@/store/nav-bar.store';
import { usePermissions } from '@/modules/users/hooks/usePermissions';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils/cn';
import { buildNavigationRoutes } from '@/modules/navigation/components/main-navigation/navigation.routes';
import { HELP_INDEX_PATH } from '@/modules/help/constants/help-routes';
import { useTranslations } from '@/lib/i18n/useTranslations';
import { NotificationsBell } from '@/modules/notifications/components';
import { ProfileAvatar } from '@/modules/users/components/ProfileAvatar';
import { useBrandStore } from '@/lib/store/brand.store';
import { useLongPressToOpenDebug } from '@/lib/debug/activation';
import { isDebugPanelEnabled } from '@/lib/debug/enabled';

function LogoWithLongPress({
  brand,
  isTextVisible,
}: {
  brand: { logoUrl?: string; legalName?: string } | null;
  isTextVisible: boolean;
}) {
  const longPressProps = useLongPressToOpenDebug();

  return (
    <Link
      to={'/dashboard'}
      className={cn(
        'flex items-center justify-center transition-opacity duration-300',
        isTextVisible ? 'opacity-0' : 'opacity-100',
      )}
      {...(isDebugPanelEnabled() ? longPressProps : {})}
    >
      <img
        src={brand?.logoUrl ?? '/ivolcan-logo.svg'}
        width={140}
        height={51}
        alt={brand?.legalName ?? 'Logo'}
        className="h-auto w-[140px] object-contain"
      />
    </Link>
  );
}

export const MainNavigation = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isTextVisible, setIsTextVisible] = useState(true);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const { isMainNavCollapsed, setIsMainNavCollapsed } = useNavBarStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canRead } = usePermissions();
  const user = useAuthStore(s => s.user);
  const brand = useBrandStore(s => s.brand);
  const logout = useAuthStore(state => state.logout);
  const t = useTranslations('Navigation');
  const canSeeNotifications = canRead('NOTIFICATIONS');

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setIsMainNavCollapsed(!isMainNavCollapsed);
  };

  const visibleNavigation = buildNavigationRoutes(pathname, {
    dashboard: t('dashboard'),
    services: t('services'),
    incidences: t('incidences'),
    inventory: t('inventory'),
    products: t('products'),
    stock: t('stock'),
    consolidatedStock: t('consolidatedStock'),
    serialNumbers: t('serialNumbers'),
    users: t('users'),
    customers: t('customers'),
    billing: t('billing'),
    quotes: t('quotes'),
    invoices: t('invoices'),
    deliveryNotes: t('deliveryNotes'),
    accounting: t('accounting'),
    purchasing: t('purchasing'),
    suppliers: t('suppliers'),
    purchaseInvoices: t('purchaseInvoices'),
    workOrders: t('workOrders'),
    calendar: t('calendar'),
    finance: t('finance'),
    accountsPayable: t('accountsPayable'),
  }).filter(item => {
    if (canRead(item.module)) {
      return true;
    }

    if (item.children?.length) {
      return item.children.some(child => canRead(child.module));
    }

    return false;
  });

  const dropdownNavigation: Array<{
    href: string;
    target?: string;
    label: string;
    module?: 'SETTINGS';
    icon: JSX.Element;
  }> = [
    {
      label: t('settings'),
      href: '/settings',
      icon: <Settings />,
      module: 'SETTINGS',
    },
    {
      label: t('help'),
      href: HELP_INDEX_PATH,
      target: '_blank',
      icon: <CircleHelp />,
    },
  ];

  const visibleDropdownNavigation = dropdownNavigation.filter(
    link => !link.module || canRead(link.module),
  );

  const toggleSection = (key: string) => {
    // Si el sidebar está colapsado, expandirlo automáticamente
    if (isCollapsed) {
      setIsCollapsed(false);
      setIsMainNavCollapsed(false);
    }
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const toggleTextOpacity = () => {
      setIsTextVisible(isCollapsed);
    };
    const timeoutId = setTimeout(toggleTextOpacity, 200);
    return () => clearTimeout(timeoutId);
  }, [isCollapsed]);

  return (
    <aside
      className={cn(
        'h-screen flex flex-col rounded-r-xl border-r border-slate-200 bg-white pt-3 shadow-md transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-56',
      )}
    >
      {/* Logo and Toggle - Fixed at top */}
      <div
        className={cn(
          'flex items-center pb-4 transition-all duration-300',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-3',
        )}
      >
        {!isCollapsed && <LogoWithLongPress brand={brand} isTextVisible={isTextVisible} />}

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className={cn(
            'rounded-xl bg-slate-50 p-1 text-slate-600 transition-all hover:bg-slate-100 focus:outline-none focus:ring-0 focus:ring-transparent',
          )}
        >
          {isCollapsed ? (
            <PanelLeftOpenIcon strokeWidth={1.5} />
          ) : (
            <PanelLeftCloseIcon strokeWidth={1.5} />
          )}
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <ul>
          {visibleNavigation.map(item => {
            const hasChildren = item.children?.length;

            if (!hasChildren) {
              return (
                <NavigationLink
                  key={item.name}
                  href={item.href}
                  isActive={item.isActive}
                  isCollapsed={isCollapsed}
                  isTextVisible={isTextVisible}
                  linkText={item.name}
                >
                  <item.icon onClick={() => toggleSection(item.name)} strokeWidth={1.5} />
                </NavigationLink>
              );
            }

            const isOpen = openSections[item.name] ?? item.isActive; // abre si está activo
            const visibleChildren = item.children?.filter(child => canRead(child.module)) ?? [];

            return (
              <section key={item.name}>
                {item.separator === 'top' && (
                  <div className="flex border-t border-slate-200 p-2"></div>
                )}
                <li key={item.name} className="px-2">
                  {/* Padre */}
                  <button
                    type="button"
                    onClick={() => toggleSection(item.name)}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-slate-700 hover:bg-slate-50',
                      item.isActive && 'bg-slate-50',
                    )}
                  >
                    <item.icon strokeWidth={1.5} />
                    {!isCollapsed && !isTextVisible && (
                      <>
                        <span className="flex-1 text-left text-sm font-medium">{item.name}</span>
                        <ChevronRightIcon
                          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-90')}
                        />
                      </>
                    )}
                  </button>

                  {!isCollapsed && !isTextVisible && isOpen && visibleChildren.length > 0 && (
                    <ul className="mt-1 space-y-1 pl-9">
                      {visibleChildren.map(child => (
                        <Link to={child.href ?? '#'} key={child.name} className="block">
                          <div
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50',
                              child.isActive && 'bg-slate-100 text-slate-900 font-semibold',
                            )}
                          >
                            <child.icon strokeWidth={1.5} />

                            {child.name}
                          </div>
                        </Link>
                      ))}
                    </ul>
                  )}
                </li>
              </section>
            );
          })}
        </ul>
      </div>

      {/* Footer - Fixed at bottom */}
      <div className="mt-auto">
        <div
          className={cn(
            'flex items-center gap-2 border-t border-slate-200 p-2',
            isCollapsed ? 'flex-col justify-center' : 'justify-between',
          )}
        >
          {/* Full page load: the help area is a public surface outside the panel. */}
          <a
            href={HELP_INDEX_PATH}
            title={t('help')}
            className={cn(
              'flex items-center gap-2 rounded-md px-2 py-1 text-sm text-slate-600 hover:bg-slate-100 hover:text-slate-900',
              isCollapsed && 'px-1',
            )}
          >
            <CircleHelp className="h-5 w-5" strokeWidth={1.5} aria-hidden="true" />
            {!isCollapsed && <span>{t('help')}</span>}
          </a>
          {canSeeNotifications && <NotificationsBell compact={isCollapsed} />}
        </div>

        {/* User Switch */}
        <div className="flex items-center">
          <DropdownMenu dir={'rtl'}>
            <DropdownMenuTrigger
              asChild
              id="userDropdownTrigger"
              className="w-full rounded-br-xl py-4 transition-colors duration-200 hover:bg-slate-50 focus:outline-none"
            >
              <div
                className={cn(
                  'flex cursor-pointer flex-row items-center gap-3',
                  isCollapsed ? 'justify-center px-2' : 'px-4',
                )}
              >
                <ProfileAvatar avatarUrl={user?.avatarUrl} userName={user?.name} />
                {!isCollapsed && !isTextVisible && (
                  <>
                    <div
                      className={cn(
                        isTextVisible ? 'opacity-0' : 'opacity-100',
                        'grow overflow-hidden',
                      )}
                    >
                      <p
                        title={user?.name}
                        className={cn(
                          'ph-no-capture ph-no-capture -mb-0.5 truncate text-sm font-bold text-slate-700',
                        )}
                      >
                        {user?.name ? <span>{user?.name}</span> : <span>{user?.email}</span>}
                      </p>
                      <p className="text-sm text-slate-700">{user?.role}</p>
                    </div>
                    <ChevronRightIcon
                      className={cn('h-5 w-5 shrink-0 text-slate-700 hover:text-slate-500')}
                    />
                  </>
                )}
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              id="userDropdownInnerContentWrapper"
              side="right"
              sideOffset={10}
              alignOffset={5}
              align="end"
            >
              {/* Dropdown Items */}

              {visibleDropdownNavigation.map((link, index) =>
                link.target ? (
                  /*
                    Real anchor as the menu item: the library item renders its
                    own children, so the primitive is used here with the
                    library row classes. This keeps href and keyboard behaviour.
                  */
                  <DropdownMenuPrimitive.Item key={`dropdown-item-${index}`} asChild>
                    <a
                      href={link.href}
                      target={link.target}
                      rel="noopener noreferrer"
                      className="oniratec-dropdown__item"
                    >
                      <span className="oniratec-dropdown__icon">{link.icon}</span>
                      {link.label}
                    </a>
                  </DropdownMenuPrimitive.Item>
                ) : (
                  <DropdownMenuItem
                    key={`dropdown-item-${index}`}
                    onClick={() => {
                      navigate(link.href);
                    }}
                    icon={link.icon}
                  >
                    {link.label}
                  </DropdownMenuItem>
                ),
              )}
              {/* Logout */}
              <DropdownMenuItem
                onClick={async () => {
                  logout();
                  navigate('/auth/login');
                }}
                icon={<LogOutIcon strokeWidth={1.5} />}
              >
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
  );
};
